import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ArrowUpDown, MessageSquare, Send, Download } from 'lucide-react';
import { vendorCostAnalysisApi } from '../../services/vendorCostAnalysisApi';
import type { VCATransactionRow, VCAComment, PaginationInfo, VCATransactionTotals, VCACompanyOption } from '../../types/vendorCostAnalysis';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    dateFrom: string;
    dateTo: string;
    company?: string;
    vendorID?: string;
    groupVendorID?: string;
    vendorIDs?: string[];
    groupVendorIDs?: string[];
    categoryCode?: string;
}

function formatEUR(n: number): string {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function formatDate(d: string): string {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function buildTransactionRef(r: VCATransactionRow): string {
    return JSON.stringify({
        date: r.postingDate,
        company: r.companyCode,
        amount: r.amountEUR,
        desc: r.description || '',
        source: 'ErpActual',
        sourceId: r.documentNr || undefined,
    });
}

export function TransactionDrillDownModal({ isOpen, onClose, title, dateFrom, dateTo, company, vendorID, groupVendorID, vendorIDs, groupVendorIDs, categoryCode }: Props) {
    const [rows, setRows] = useState<VCATransactionRow[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('postingDate');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [search, setSearch] = useState('');
    const [comments, setComments] = useState<Map<string, VCAComment[]>>(new Map());
    const [commentingRef, setCommentingRef] = useState<string | null>(null);
    const [commentText, setCommentText] = useState('');
    const [commentSaving, setCommentSaving] = useState(false);
    const [totals, setTotals] = useState<VCATransactionTotals | null>(null);
    const [availableCompanies, setAvailableCompanies] = useState<VCACompanyOption[]>([]);
    const [modalCompany, setModalCompany] = useState('');
    const [exporting, setExporting] = useState(false);

    // Reset modal-specific state when modal opens with new context.
    // vendorIDs / groupVendorIDs are arrays — depend on their length + first id to avoid
    // re-resetting on every parent render.
    const vendorIDsKey = vendorIDs?.join(',') || '';
    const groupVendorIDsKey = groupVendorIDs?.join(',') || '';
    useEffect(() => {
        if (isOpen) {
            setModalCompany('');
            setAvailableCompanies([]);
            setSearch('');
            setPage(1);
        }
    }, [isOpen, vendorID, groupVendorID, vendorIDsKey, groupVendorIDsKey, categoryCode]);

    // Load transactions
    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        const effectiveCompany = modalCompany || company || undefined;
        vendorCostAnalysisApi.getTransactions({
            dateFrom, dateTo,
            company: effectiveCompany,
            vendorID: vendorID || undefined,
            groupVendorID: groupVendorID || undefined,
            vendorIDs: vendorIDs && vendorIDs.length > 0 ? vendorIDs : undefined,
            groupVendorIDs: groupVendorIDs && groupVendorIDs.length > 0 ? groupVendorIDs : undefined,
            categoryCode: categoryCode || undefined,
            search: search || undefined,
            sortBy, sortOrder,
            page, limit: 50,
        })
            .then(res => {
                setRows(res.items);
                setPagination(res.pagination);
                setTotals(res.totals);
                // Only capture company list when the modal company filter is unset,
                // so the dropdown always shows every company for this drill-down.
                if (!modalCompany) {
                    setAvailableCompanies(res.companies);
                }
            })
            .catch(() => { setRows([]); setTotals(null); })
            .finally(() => setLoading(false));
    }, [isOpen, dateFrom, dateTo, company, modalCompany, vendorID, groupVendorID, vendorIDsKey, groupVendorIDsKey, categoryCode, search, sortBy, sortOrder, page]);

    // Load comments for visible transactions
    useEffect(() => {
        if (!isOpen || rows.length === 0) return;
        const refs = rows.map(buildTransactionRef);
        vendorCostAnalysisApi.getComments(refs)
            .then(allComments => {
                const map = new Map<string, VCAComment[]>();
                for (const c of allComments) {
                    const existing = map.get(c.transactionRef) || [];
                    existing.push(c);
                    map.set(c.transactionRef, existing);
                }
                setComments(map);
            })
            .catch(() => setComments(new Map()));
    }, [isOpen, rows]);

    const handleSort = (col: string) => {
        if (sortBy === col) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(col);
            setSortOrder('DESC');
        }
        setPage(1);
    };

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            const effectiveCompany = modalCompany || company || undefined;
            await vendorCostAnalysisApi.exportTransactions({
                dateFrom, dateTo,
                company: effectiveCompany,
                vendorID: vendorID || undefined,
                groupVendorID: groupVendorID || undefined,
                vendorIDs: vendorIDs && vendorIDs.length > 0 ? vendorIDs : undefined,
                groupVendorIDs: groupVendorIDs && groupVendorIDs.length > 0 ? groupVendorIDs : undefined,
                categoryCode: categoryCode || undefined,
                search: search || undefined,
            });
        } catch { /* ignore */ }
        setExporting(false);
    }, [dateFrom, dateTo, company, modalCompany, vendorID, groupVendorID, vendorIDsKey, groupVendorIDsKey, categoryCode, search]);

    const handleAddComment = useCallback(async (ref: string, row: VCATransactionRow) => {
        if (!commentText.trim()) return;
        setCommentSaving(true);
        try {
            const d = new Date(row.postingDate);
            const newComment = await vendorCostAnalysisApi.createComment({
                transactionRef: ref,
                commentText: commentText.trim(),
                periodYear: d.getFullYear(),
                periodMonth: d.getMonth() + 1,
                companyCode: row.companyCode,
                accountCode: row.accountNr,
            });
            // Update local comment map
            setComments(prev => {
                const next = new Map(prev);
                const existing = next.get(ref) || [];
                next.set(ref, [newComment, ...existing]);
                return next;
            });
            setCommentText('');
            setCommentingRef(null);
        } catch { /* ignore */ }
        setCommentSaving(false);
    }, [commentText]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-6xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExport}
                            disabled={exporting || loading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50 disabled:opacity-50"
                            title="Download all rows (no pagination) as Excel"
                        >
                            <Download className="w-3.5 h-3.5" />
                            {exporting ? 'Exporting…' : 'Download Excel'}
                        </button>
                        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Search + company filter */}
                <div className="px-6 py-3 border-b flex flex-wrap items-center gap-3">
                    <input
                        type="text"
                        placeholder="Search vendor, description, document..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        className="w-full md:w-80 border rounded-md px-3 py-1.5 text-sm"
                    />
                    {availableCompanies.length > 1 && (
                        <select
                            value={modalCompany}
                            onChange={e => { setModalCompany(e.target.value); setPage(1); }}
                            className="border rounded-md px-3 py-1.5 text-sm bg-white"
                            title="Filter by company"
                        >
                            <option value="">All companies ({availableCompanies.reduce((s, c) => s + c.count, 0)})</option>
                            {availableCompanies.map(c => (
                                <option key={c.code} value={c.code}>{c.code} ({c.count})</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto px-6 py-2">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white">
                                <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b">
                                    <th className="py-2 pr-1 w-8"></th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('companyCode')}>
                                        <span className="flex items-center gap-1">Co <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('postingDate')}>
                                        <span className="flex items-center gap-1">Date <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('vendorName')}>
                                        <span className="flex items-center gap-1">Vendor <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('accountNr')}>
                                        <span className="flex items-center gap-1">Account <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('categoryName')}>
                                        <span className="flex items-center gap-1">Category <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('documentNr')}>
                                        <span className="flex items-center gap-1">Document <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 cursor-pointer" onClick={() => handleSort('description')}>
                                        <span className="flex items-center gap-1">Description <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 pr-3 text-right cursor-pointer" onClick={() => handleSort('amountEUR')}>
                                        <span className="flex items-center gap-1 justify-end">EUR <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                    <th className="py-2 text-right cursor-pointer" onClick={() => handleSort('amountLCY')}>
                                        <span className="flex items-center gap-1 justify-end">LCY <ArrowUpDown className="w-3 h-3" /></span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const ref = buildTransactionRef(r);
                                    const rowComments = comments.get(ref) || [];
                                    const hasComments = rowComments.length > 0;
                                    const isCommenting = commentingRef === ref;

                                    return (
                                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 group">
                                            <td className="py-2 pr-1">
                                                <button
                                                    onClick={() => {
                                                        if (isCommenting) {
                                                            setCommentingRef(null);
                                                            setCommentText('');
                                                        } else {
                                                            setCommentingRef(ref);
                                                            setCommentText('');
                                                        }
                                                    }}
                                                    className={`p-0.5 rounded ${hasComments ? 'text-blue-600' : 'text-gray-300 opacity-0 group-hover:opacity-100'} hover:bg-blue-50`}
                                                    title={hasComments ? `${rowComments.length} comment(s)` : 'Add comment'}
                                                >
                                                    <MessageSquare className="w-3.5 h-3.5" fill={hasComments ? 'currentColor' : 'none'} />
                                                </button>
                                            </td>
                                            <td className="py-2 pr-3 text-xs text-gray-500">{r.companyCode}</td>
                                            <td className="py-2 pr-3 whitespace-nowrap">{formatDate(r.postingDate)}</td>
                                            <td className="py-2 pr-3">
                                                <div className="font-medium">{r.vendorName}</div>
                                                <div className="text-xs text-gray-400">{r.vendorCode}</div>
                                            </td>
                                            <td className="py-2 pr-3">
                                                <div>{r.accountName}</div>
                                                <div className="text-xs text-gray-400">{r.accountNr}</div>
                                            </td>
                                            <td className="py-2 pr-3 text-xs">{r.categoryName}</td>
                                            <td className="py-2 pr-3 text-xs font-mono">{r.documentNr}</td>
                                            <td className="py-2 pr-3 text-xs max-w-[200px] truncate" title={r.description}>{r.description}</td>
                                            <td className="py-2 pr-3 text-right font-mono whitespace-nowrap">
                                                <span className={r.amountEUR >= 0 ? '' : 'text-green-600'}>
                                                    {formatEUR(r.amountEUR)}
                                                </span>
                                            </td>
                                            <td className="py-2 text-right text-xs text-gray-400 whitespace-nowrap">
                                                {formatEUR(r.amountLCY)} {r.currencyCode}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {/* Inline comment form */}
                                {commentingRef && rows.some(r => buildTransactionRef(r) === commentingRef) && (
                                    <tr className="bg-blue-50">
                                        <td colSpan={10} className="py-2 px-3">
                                            <div className="flex items-center gap-2">
                                                <MessageSquare className="w-4 h-4 text-blue-500 shrink-0" />
                                                <input
                                                    type="text"
                                                    value={commentText}
                                                    onChange={e => setCommentText(e.target.value)}
                                                    placeholder="Add a comment..."
                                                    className="flex-1 border rounded-md px-3 py-1.5 text-sm"
                                                    autoFocus
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' && commentText.trim()) {
                                                            const row = rows.find(r => buildTransactionRef(r) === commentingRef);
                                                            if (row) handleAddComment(commentingRef, row);
                                                        }
                                                        if (e.key === 'Escape') {
                                                            setCommentingRef(null);
                                                            setCommentText('');
                                                        }
                                                    }}
                                                />
                                                <button
                                                    onClick={() => {
                                                        const row = rows.find(r => buildTransactionRef(r) === commentingRef);
                                                        if (row) handleAddComment(commentingRef, row);
                                                    }}
                                                    disabled={commentSaving || !commentText.trim()}
                                                    className="p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                                >
                                                    <Send className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => { setCommentingRef(null); setCommentText(''); }}
                                                    className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-md"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                            {/* Show existing comments */}
                                            {(comments.get(commentingRef) || []).map(c => (
                                                <div key={c.commentId} className="mt-2 ml-6 text-xs text-gray-600 bg-white rounded px-3 py-2 border">
                                                    <span className="font-medium">{c.createdBy.split('@')[0]}</span>
                                                    <span className="text-gray-400 ml-2">{formatDate(c.createdDate)}</span>
                                                    <p className="mt-0.5">{c.commentText}</p>
                                                </div>
                                            ))}
                                        </td>
                                    </tr>
                                )}
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="py-8 text-center text-gray-400">No transactions found</td>
                                    </tr>
                                )}
                            </tbody>
                            {totals && rows.length > 0 && (
                                <tfoot className="sticky bottom-0 bg-gray-50">
                                    <tr className="border-t-2 border-gray-300 font-semibold">
                                        <td className="py-2 pr-1"></td>
                                        <td className="py-2 pr-3 text-xs text-gray-500" colSpan={7}>
                                            Total ({pagination?.total ?? rows.length} transactions)
                                        </td>
                                        <td className="py-2 pr-3 text-right font-mono whitespace-nowrap">
                                            <span className={totals.totalEUR >= 0 ? '' : 'text-green-600'}>
                                                {formatEUR(totals.totalEUR)}
                                            </span>
                                        </td>
                                        <td className="py-2 text-right text-xs text-gray-500 whitespace-nowrap">
                                            {totals.totalLCY.length === 0 ? (
                                                <span className="text-gray-300">-</span>
                                            ) : totals.totalLCY.length === 1 ? (
                                                <>{formatEUR(totals.totalLCY[0].total)} {totals.totalLCY[0].currencyCode}</>
                                            ) : (
                                                <span title={totals.totalLCY.map(l => `${formatEUR(l.total)} ${l.currencyCode}`).join('\n')}>
                                                    {totals.totalLCY.length} currencies
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                    {totals.totalLCY.length > 1 && (
                                        <tr className="bg-gray-50 text-xs text-gray-500">
                                            <td></td>
                                            <td colSpan={8} className="py-1 pr-3 text-right">by currency:</td>
                                            <td className="py-1 text-right whitespace-nowrap">
                                                {totals.totalLCY.map(l => (
                                                    <div key={l.currencyCode} className="font-mono">
                                                        {formatEUR(l.total)} {l.currencyCode}
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    )}
                                </tfoot>
                            )}
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-3 border-t text-sm text-gray-500">
                        <span>{pagination.total} transactions total</span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={!pagination.hasPrev}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span>Page {pagination.page} of {pagination.totalPages}</span>
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={!pagination.hasNext}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
